import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { fetchPage } from "@/lib/api";
import Seo from "@/components/public/Seo";
import { withSiteName } from "@/lib/seo";

export default function ContentPage({ slug, initialPage }) {
  const initialForRoute = initialPage?.slug === slug ? initialPage : null;
  const [page, setPage] = useState(initialForRoute);
  const [loading, setLoading] = useState(!initialForRoute);
  const [loadError, setLoadError] = useState(initialForRoute?.__ssrError === true && initialForRoute.status !== 404);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (initialForRoute) return undefined;
    let active = true;
    setLoading(true);
    setLoadError(false);
    fetchPage(slug)
      .then((result) => {
        if (!active) return;
        if (
          !result ||
          typeof result !== "object" ||
          typeof result.title !== "string" ||
          typeof result.content !== "string"
        ) {
          setPage(null);
          setLoadError(true);
          return;
        }
        setPage(result);
      })
      .catch((error) => {
        if (!active) return;
        setPage(null);
        setLoadError(error.response?.status !== 404);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // SSR props are initial-only; later route mounts receive no SSR payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, attempt]);

  if (loading) {
    return (
      <div className="flex justify-center py-20" role="status" aria-live="polite" aria-label="جارٍ تحميل الصفحة">
        <Loader2 className="w-7 h-7 animate-spin text-primary" aria-hidden="true" />
        <span className="sr-only">جارٍ تحميل الصفحة…</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <>
        <Seo title="تعذّر تحميل الصفحة — ARAN TOOLS" description="تعذّر تحميل هذه الصفحة حالياً." noIndex />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center" dir="rtl">
          <div role="alert" className="aran-card p-6">
            <p className="text-muted-foreground">تعذّر تحميل الصفحة حالياً. حاول مرة أخرى.</p>
            <button type="button" onClick={() => setAttempt((value) => value + 1)} className="mt-4 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              إعادة المحاولة
            </button>
          </div>
        </main>
      </>
    );
  }

  if (!page) {
    return (
      <>
        <Seo title="الصفحة غير موجودة — ARAN TOOLS" description="الصفحة التي تبحث عنها غير موجودة." noIndex />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center text-muted-foreground">
          الصفحة غير موجودة.
        </div>
      </>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-12 pb-6">
      <Seo title={withSiteName(page.meta_title || page.title)} description={page.meta_description || page.content.slice(0, 160)} path={`/${slug}`} />
      <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">{page.title}</h1>
      <div className="aran-card p-6 sm:p-8">
        <p className="text-base leading-loose text-foreground/90 whitespace-pre-line">{page.content}</p>
      </div>
    </div>
  );
}
